export const APP_ROLES = [
  'admin',
  'absence_management',
  'service_management',
  'lamm_overseer',
  'planner',
  'read_only',
  'overseer',
] as const;

export type AppRole = (typeof APP_ROLES)[number];

export const PLAN_CYCLE_APP_ROLES = APP_ROLES.filter(
  role => role !== 'planner' && role !== 'read_only'
);

export const PERMISSIONS = {
  fieldServiceRead: 'field_service.read',
  fieldServiceWrite: 'field_service.write',
  dashboardRead: 'dashboard.read',
  absencesRead: 'absences.read',
  absencesWrite: 'absences.write',
  servicesRead: 'services.read',
  servicesWrite: 'services.write',
  lammRead: 'lamm.read',
  lammWrite: 'lamm.write',
  publishersRead: 'publishers.read',
  publishersWrite: 'publishers.write',
  exportsRead: 'exports.read',
  exportsWrite: 'exports.write',
  usersRead: 'users.read',
  usersWrite: 'users.write',
  permissionsManage: 'permissions.manage',
  deepLinksManage: 'deep_links.manage',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

const allPermissions = Object.values(PERMISSIONS);

const rolePermissions: Record<AppRole, Permission[]> = {
  admin: allPermissions,
  absence_management: [
    PERMISSIONS.dashboardRead,
    PERMISSIONS.absencesRead,
    PERMISSIONS.absencesWrite,
    PERMISSIONS.publishersRead,
    PERMISSIONS.deepLinksManage,
  ],
  service_management: [
    PERMISSIONS.dashboardRead,
    PERMISSIONS.absencesRead,
    PERMISSIONS.servicesRead,
    PERMISSIONS.servicesWrite,
    PERMISSIONS.publishersRead,
  ],
  lamm_overseer: [
    PERMISSIONS.dashboardRead,
    PERMISSIONS.absencesRead,
    PERMISSIONS.absencesWrite,
    PERMISSIONS.publishersWrite,
    PERMISSIONS.lammRead,
    PERMISSIONS.lammWrite,
    PERMISSIONS.publishersRead,
  ],
  read_only: [
    PERMISSIONS.dashboardRead,
    PERMISSIONS.absencesRead,
    PERMISSIONS.publishersRead,
    PERMISSIONS.lammRead,
    PERMISSIONS.servicesRead,
  ],
  planner: [
    PERMISSIONS.dashboardRead,
    PERMISSIONS.absencesRead,
    PERMISSIONS.publishersRead,
    PERMISSIONS.lammRead,
    PERMISSIONS.servicesRead,
    PERMISSIONS.fieldServiceRead,
    PERMISSIONS.fieldServiceWrite,
  ],
  overseer: allPermissions.filter(permission => permission !== PERMISSIONS.permissionsManage),
};

export const normalizeAppRole = (
  role?: string | null,
  fallback: AppRole = 'read_only'
): AppRole => {
  if (!role) return fallback;
  return (APP_ROLES as readonly string[]).includes(role) ? (role as AppRole) : fallback;
};

export const getPermissionsForRole = (role?: string | null): Permission[] =>
  rolePermissions[normalizeAppRole(role)];
